import React, { useState, useRef } from "react";
import {
  UploadIcon,
  Document2Icon,
  ArrowDownBlackIcon,
} from "~/assets/icons";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import jobDetailsApi from "~/apis/jobDetails";
import { notify } from "~/utils/helper";
import FormUploadFile from "~/components/common/form/formUploadFIle/FormUploadFile";
import BaseSelectCustom from "~/components/common/base-select-custom/BaseSelectCustom";
import styles from "./DocumentTab.module.scss";
import classNames from "classnames/bind";

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  jobId: string;
  categoryOptions: Array<{ id: string; label: string }>;
  visibilityOptions: Array<{ id: string; label: string }>;
}

interface UploadData {
  file: File | null;
  category: string;
  visibility: string;
  description: string;
}


const cx = classNames.bind(styles);

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  jobId,
  categoryOptions,
  visibilityOptions,
}) => {
  const [uploadData, setUploadData] = useState<UploadData>({
    file: null,
    category: "",
    visibility: "",
    description: "",
  });
  const [isDragActive, setIsDragActive] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setUploadData({ file: null, category: "", visibility: "", description: "" });
    setIsDragActive(false);
    setShowCategoryDropdown(false);
    setShowVisibilityDropdown(false);
    onClose();
  };

  const handleFileSelect = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit
      notify("error", "File size must be less than 10MB");
      return;
    }

    // Check file extension
    const extension = file.name.split(".").pop()?.toLowerCase();
    const allowedExtensions = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg"];

    if (!extension || !allowedExtensions.includes(extension)) {
      notify("error", "File type not supported. Allowed types: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG");
      return;
    }

    setUploadData({ ...uploadData, file });
  };

  const onChangeFile = (formData: any) => {
    setUploadData({ ...uploadData, file: formData.file });
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const getFileType = (file: File): string => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension === "pdf") return "PDF";
    if (["xls", "xlsx"].includes(extension || "")) return "EXCEL";
    if (["doc", "docx"].includes(extension || "")) return "WORD";
    return "PDF";
  };

  const handleSubmit = async () => {
    if (!uploadData.file) {
      notify("error", "Please select a file");
      return;
    }
    if (!uploadData.category) {
      notify("error", "Please select a category");
      return;
    }
    if (!uploadData.visibility) {
      notify("error", "Please select visibility");
      return;
    }
    if (!jobId) {
      notify("error", "Job ID not found");
      return;
    }

    try {
      setIsUploading(true);
      const fileType = getFileType(uploadData.file);
      const categoryName = categoryOptions.find((opt) => opt.id === uploadData.category)?.label || "";
      const visibilityName = visibilityOptions.find((opt) => opt.id === uploadData.visibility)?.label || "";

      await jobDetailsApi.createJobDocument({
        file: uploadData.file,
        category: categoryName,
        fileSize: uploadData.file.size.toString(),
        fileType: fileType,
        fileName: uploadData.file.name,
        description: uploadData.description || "",
        jobId: parseInt(jobId),
        visibility: visibilityName,
      });

      notify("success", "Document uploaded successfully");
      handleClose();
      onSuccess();
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div
        className="bg-[#0C192D] rounded-lg w-full max-w-[600px] mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <h2 className="text-xl font-semibold text-white">Upload Document</h2>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File Upload Area */}
          {/* <div>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragActive
                  ? "border-[#3b82f6] bg-[#1f2937]"
                  : "border-[#1f2937] bg-[#071427]"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadData.file ? (
                <div className="space-y-2">
                  <Document2Icon className="w-12 h-12 text-[#3b82f6] mx-auto" />
                  <p className="text-white text-sm font-medium">{uploadData.file.name}</p>
                  <p className="text-[#A2A7B4] text-xs">
                    {(uploadData.file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center space-y-2 h-[100px] text-[#949699]">
                  <p className="text-sm text-center text-[#949699]">
                    Drag and drop your file here or
                  </p>
                  <p className="text-xs text-center text-[#949699]">Maximum file size: 10MB</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileInputChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg"
              />
            </div>
          </div> */}

          <FormUploadFile
            onChange={onChangeFile} />

          {/* Category Select */}
          <div className="relative">
            <label className="block text-sm font-medium mb-2 text-[#949699]">
              Category
            </label>

            <BaseSelectCustom
              selectedView={uploadData.category}
              onViewChange={(viewId) => {
                if (viewId)
                  setUploadData({ ...uploadData, category: viewId.toString() });
              }}
              height="44px"
              viewOptions={categoryOptions}
              placeholder="Select"
              className={cx('select-filter')}
              minWidth={'100%'}
              showCheckmark={false}
              IconDrop={
                <div className={cx('drop')}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.5648 13.873L8.88781 11.196C8.85114 11.1593 8.82015 11.1163 8.79481 11.067C8.77015 11.0183 8.75781 10.966 8.75781 10.91C8.75781 10.798 8.79448 10.7017 8.86781 10.621C8.94248 10.5403 9.04048 10.5 9.16181 10.5H14.8388C14.9595 10.5 15.0568 10.5413 15.1308 10.624C15.2048 10.7067 15.2418 10.8027 15.2418 10.912C15.2418 10.94 15.1985 11.0347 15.1118 11.196L12.4348 13.873C12.3728 13.935 12.3061 13.9827 12.2348 14.016C12.1635 14.0493 12.0851 14.066 11.9998 14.066C11.9145 14.066 11.8361 14.0493 11.7648 14.016C11.6935 13.9827 11.6268 13.935 11.5648 13.873Z" fill="white" />
                  </svg>
                </div>
              }
            />

            {/* <button
              type="button"
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 bg-[#D9D9D9] text-[#000000] rounded-lg hover:bg-[#C9C9C9] transition-colors"
            >
              <span className="text-sm">
                {uploadData.category
                  ? categoryOptions.find((opt) => opt.id === uploadData.category)?.label
                  : "Select category"}
              </span>
              <ArrowDownBlackIcon className="w-2 h-1" />
            </button>
            {showCategoryDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowCategoryDropdown(false)}
                />
                <div className="absolute top-full mt-2 left-0 right-0 z-20 bg-white rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {categoryOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setUploadData({ ...uploadData, category: option.id });
                        setShowCategoryDropdown(false);
                      }}
                      className={`w-full text-center px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors text-sm font-medium text-[#111E33] ${
                        uploadData.category === option.id
                          ? "bg-gray-50"
                          : ""
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )} */}
          </div>

          {/* Visibility Select */}
          <div className="relative">
            <label className="block text-sm font-medium mb-2 text-[#949699]">
              Visibility
            </label>

            <BaseSelectCustom
              selectedView={uploadData.visibility}
              onViewChange={(viewId) => {
                if (viewId)
                  setUploadData({ ...uploadData, visibility: viewId.toString() });
              }}
              height="44px"
              viewOptions={visibilityOptions}
              placeholder="Select"
              className={cx('select-filter')}
              minWidth={'100%'}
              showCheckmark={false}
              IconDrop={
                <div className={cx('drop')}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.5648 13.873L8.88781 11.196C8.85114 11.1593 8.82015 11.1163 8.79481 11.067C8.77015 11.0183 8.75781 10.966 8.75781 10.91C8.75781 10.798 8.79448 10.7017 8.86781 10.621C8.94248 10.5403 9.04048 10.5 9.16181 10.5H14.8388C14.9595 10.5 15.0568 10.5413 15.1308 10.624C15.2048 10.7067 15.2418 10.8027 15.2418 10.912C15.2418 10.94 15.1985 11.0347 15.1118 11.196L12.4348 13.873C12.3728 13.935 12.3061 13.9827 12.2348 14.016C12.1635 14.0493 12.0851 14.066 11.9998 14.066C11.9145 14.066 11.8361 14.0493 11.7648 14.016C11.6935 13.9827 11.6268 13.935 11.5648 13.873Z" fill="white" />
                  </svg>
                </div>
              }
            />

            {/* <button
              type="button"
              onClick={() => setShowVisibilityDropdown(!showVisibilityDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 bg-[#D9D9D9] text-[#000000] rounded-lg hover:bg-[#C9C9C9] transition-colors"
            >
              <span className="text-sm">
                {uploadData.visibility
                  ? visibilityOptions.find((opt) => opt.id === uploadData.visibility)?.name
                  : "Select visibility"}
              </span>
              <ArrowDownBlackIcon className="w-2 h-1" />
            </button>
            {showVisibilityDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowVisibilityDropdown(false)}
                />
                <div className="absolute top-full mt-2 left-0 right-0 z-20 bg-white rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {visibilityOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setUploadData({ ...uploadData, visibility: option.id });
                        setShowVisibilityDropdown(false);
                      }}
                      className={`w-full text-center px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors text-sm font-medium text-[#111E33] ${
                        uploadData.visibility === option.id
                          ? "bg-gray-50"
                          : ""
                      }`}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              </>
            )} */}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2 text-[#949699]">
              Description
            </label>
            <textarea
              value={uploadData.description}
              onChange={(e) =>
                setUploadData({ ...uploadData, description: e.target.value })
              }
              placeholder="Enter description"
              rows={4}
              className="w-full px-4 py-3 bg-[#071427] border border-[#1f2937] rounded-lg text-white placeholder-[#A2A7B4] focus:outline-none focus:border-[#3b82f6] resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6">
          <OutlinedButton
            title="Cancel"
            onClick={handleClose}
            className="!bg-transparent !text-[#949699] !border-[#1A2B47] !border-[1px] hover:!bg-[#1f2937] !rounded-[8px]"
          />
          <PrimaryButton
            title="Upload"
            onClick={handleSubmit}
            disabled={isUploading}
            loading={isUploading}
            className="!bg-[#2D80FC] !text-white hover:!bg-opacity-90 !rounded-[8px]"
          />
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentModal;

